'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { FaBookOpen } from 'react-icons/fa'

// Resolved visibility per storageKey, cached at module scope so it survives remounts.
// The image/video inputs panels unmount on every tab switch; without this the banner
// would reset to hidden and pop back in a frame later (a visible flicker). The cache
// lets a remount render the right state synchronously — only the first-ever mount goes
// through the effect (keeping SSR/first paint hidden, so no hydration mismatch).
const shouldShowCache = new Map()

// Distinct storageKey/href per panel (image vs video) so each dismisses independently.
const TutorialBanner = ({ storageKey, href, label }) => {
  const [show, setShow] = useState(
    () => shouldShowCache.get(storageKey) ?? false,
  )

  // Resolve from localStorage once (unavailable during SSR); cache so later remounts
  // read it synchronously via the initializer above instead of flashing.
  useEffect(() => {
    if (shouldShowCache.has(storageKey)) return
    try {
      const shouldShow = !localStorage.getItem(storageKey)
      shouldShowCache.set(storageKey, shouldShow)
      setShow(shouldShow)
    } catch {
      // localStorage blocked (e.g. private mode) — keep hidden.
      shouldShowCache.set(storageKey, false)
    }
  }, [storageKey])

  const dismiss = () => {
    try {
      localStorage.setItem(storageKey, '1')
    } catch {
      // Ignore write failures — worst case it shows again next visit.
    }
    shouldShowCache.set(storageKey, false)
    setShow(false)
  }

  if (!show) return null

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={dismiss}
      className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 ring-2 ring-[var(--main-bg-color)] bg-[var(--middle-bg-color)] hover:bg-[var(--main-shade-bg-color)] transition-colors"
    >
      <FaBookOpen className="text-[var(--main-bg-color)] text-lg shrink-0" />
      <span className="text-white font-semibold text-sm tracking-tight">
        {label}
      </span>
    </Link>
  )
}

export default TutorialBanner
